import { useState, useRef } from 'react';
import * as S from './CustomTextarea.styles';
import { useTranslation } from 'react-i18next';

interface CustomTextareaProps {
  onChange: (value: string) => void;
  allowClipboardActions: boolean;
}

const CustomTextarea: React.FC<CustomTextareaProps> = ({ onChange, allowClipboardActions }) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const internalClipboard = useRef('');

  // Ngăn chặn Copy, Cut, Paste bằng phím tắt (chỉ cho phép thực hiện qua nút)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!allowClipboardActions && (e.ctrlKey || e.metaKey) && ['c', 'x', 'v'].includes(e.key.toLowerCase())) {
      e.preventDefault();
    }
  };

  // Ngăn Paste từ ngoài vào
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!allowClipboardActions) {
      e.preventDefault();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    if (onChange) onChange(e.target.value);
  };

  // Thực hiện Copy (chỉ nội bộ)
  const handleCopy = () => {
    if (textareaRef.current) {
      internalClipboard.current = textareaRef.current.value.substring(
        textareaRef.current.selectionStart,
        textareaRef.current.selectionEnd,
      );
    }
  };

  // Thực hiện Cut (chỉ nội bộ)
  const handleCut = () => {
    if (textareaRef.current) {
      const { selectionStart, selectionEnd } = textareaRef.current;
      internalClipboard.current = textareaRef.current.value.substring(selectionStart, selectionEnd);

      // Xóa nội dung đã cắt
      const newText = text.slice(0, selectionStart) + text.slice(selectionEnd);
      setText(newText);
      if (onChange) onChange(newText);
    }
  };

  // Thực hiện Paste (chỉ nội bộ)
  const handlePasteClick = () => {
    if (textareaRef.current) {
      const { selectionStart, selectionEnd } = textareaRef.current;

      // Chỉ paste nội dung đã cắt/copy từ textarea này
      const newText = text.slice(0, selectionStart) + internalClipboard.current + text.slice(selectionEnd);

      setText(newText);
      if (onChange) onChange(newText);
    }
  };

  return (
    <S.Container>
      <S.Toolbar>
        <S.ToolbarButtons>
          <span onClick={handleCopy}>{t('common.copy')}</span>
          <span onClick={handleCut}>{t('common.cut')}</span>
          <span onClick={handlePasteClick}>{t('common.paste')}</span>
        </S.ToolbarButtons>
        <S.WordCount>
          {text.split(/\s+/).filter(Boolean).length} {t('common.words')}
        </S.WordCount>
      </S.Toolbar>

      <S.StyledTextarea
        ref={textareaRef}
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={t('common.writeAns')}
      />
    </S.Container>
  );
};

export default CustomTextarea;
